

import {
  Table,
  Button,
  Tag,
  TableColumnsType,
  DatePicker,
  Space,
  Modal,
  Tooltip,
  Spin
} from "antd";
import { useEffect, useMemo, useState } from "react";
import dayjs, { Dayjs } from "dayjs";
import AppLayout from "../../components/Layout";
import {
  keepPreviousData,
  useQuery,
} from "@tanstack/react-query";
import {
  downloadCallLogReportExcel,
  getCallLogReport,
  getRecordingUrl,
} from "../../services/commonController";
import { decryptData } from "../../utils";
import { DownloadOutlined, FileExcelOutlined, PlayCircleOutlined } from "@ant-design/icons";
import { generatePath, useNavigate } from "react-router-dom";

const { RangePicker } = DatePicker;

interface CallLog {
  loanId: string;
  caseId: string;
  customerName: string;
  recording: string; // Replace with callUuid if available
  callStartTime: string;
  callEndTime: string;
  callDate: string;
  transcript: string;
}

interface CallLogResponse {
  data: CallLog[];
  status: number;
  message: string;
  currentPage: number;
  totalPages: number;
  pageSize: number;
  totalResults: number;
}

const CallLogs = () => {
const today = dayjs();

const [dateRange, setDateRange] = useState<
  [Dayjs | null, Dayjs | null]
>([today, today]);

  const [callId, setCallId] = useState("");
  const [caseId, setCaseId] = useState("");
  const [isDownloadingAudio, setIsDownloadingAudio] = useState(false);
  const [isAudioModalOpen, setIsAudioModalOpen] = useState(false);
  const [audioUrl, setAudioUrl] = useState("");
const [isDownloadingExcel, setIsDownloadingExcel] = useState(false);
  const startDate = dateRange[0]?.format("YYYY-MM-DD") ?? "";
  const endDate = dateRange[1]?.format("YYYY-MM-DD") ?? "";
 const navigate = useNavigate();
  const {
    data: callLogs,
    isPending,
  } = useQuery({
    queryKey: ["callLogs", startDate, endDate],
    queryFn: () => getCallLogReport(startDate, endDate),
    placeholderData: keepPreviousData,
    enabled: !!startDate && !!endDate,
  });

  const { data: recordingUrlResponse } = useQuery({
    queryKey: ["recording-url", callId],
    queryFn: () =>
      getRecordingUrl({
        callId,
        caseId,
      }),
    enabled: !!callId,
  });

  useEffect(() => {
    if (
      callId &&
      isDownloadingAudio &&
      recordingUrlResponse?.data?.status === 200
    ) {
      const decryptedUrl = decryptData(recordingUrlResponse.data.data);

      const link = document.createElement("a");
      link.href = decryptedUrl;
      link.download = "recording.mp3";

      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);

      setCallId("");
      setCaseId("");
      setIsDownloadingAudio(false);
    }
  }, [callId, isDownloadingAudio, recordingUrlResponse]);

  const handlePlayRecording = (
    e: React.MouseEvent,
    record: CallLog
  ) => {
    e.stopPropagation();

    try {
      const decryptedUrl = decryptData(record.recording);

      setAudioUrl(decryptedUrl);
      setIsAudioModalOpen(true);
    } catch (err) {
      console.error("Unable to play recording", err);
    }
  };

  const handleDownloadRecording = (
    e: React.MouseEvent,
    record: CallLog
  ) => {
    e.stopPropagation();

    try {
      const decryptedUrl = decryptData(record.recording);

      const link = document.createElement("a");
      link.href = decryptedUrl;
      link.download = `recording-${record.loanId}.mp3`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    } catch (err) {
      console.error("Download failed", err);
    }
  };

  const columns: TableColumnsType<CallLog> = useMemo(
    () => [
      {
        title: "Loan ID",
        dataIndex: "loanId",
        key: "loanId",
        render: (value: string) => value || "-",
      },
      {
        title: "Customer Name",
        dataIndex: "customerName",
        key: "customerName",
        render: (value: string) => value || "-",
      },
       {
        title: "Agent Name",
        dataIndex: "agent",
        key: "agent",
        render: (value: string) => value || "-",
      },
      {
        title: "Start Time",
        dataIndex: "callStartTime",
        key: "callStartTime",
      },
      {
        title: "End Time",
        dataIndex: "callEndTime",
        key: "callEndTime",
      },
      {
  title: "Recording",
  key: "recording",
  render: (_, record) => (
    <Space>
      <Tooltip title="Play Recording">
        <PlayCircleOutlined
          style={{
            fontSize: 18,
            color: record.recording ? "#1677ff" : "#bfbfbf",
            cursor: record.recording ? "pointer" : "not-allowed",
          }}
          onClick={(e) =>
            record.recording && handlePlayRecording(e, record)
          }
        />
      </Tooltip>

      <Tooltip title="Download Recording">
        <Button
          type="link"
          disabled={!record.recording}
          icon={<DownloadOutlined />}
          onClick={(e) => handleDownloadRecording(e, record)}
        />
      </Tooltip>
    </Space>
  ),
}

    ],
    []
  );

  const handleDateChange = (
    dates: [Dayjs | null, Dayjs | null] | null
  ) => {
    if (!dates) return;
    setDateRange(dates);
  };

 const handleDownloadExcel = async () => {
  try {
    setIsDownloadingExcel(true);

    const response = await downloadCallLogReportExcel(
      startDate,
      endDate
    );

    const blob = new Blob([response.data], {
      type: response.headers["content-type"],
    });

    const url = window.URL.createObjectURL(blob);

    const link = document.createElement("a");
    link.href = url;
    link.download = `Call-Logs-${startDate}-to-${endDate}.xlsx`;

    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);

    window.URL.revokeObjectURL(url);
  } catch (error) {
    console.error("Failed to download call log report", error);
  } finally {
    setIsDownloadingExcel(false);
  }
};


  return (
    <AppLayout moduleName="Call Logs">
    <div
  style={{
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    marginBottom: 16,
  }}
>
  <RangePicker
    value={dateRange}
    onChange={handleDateChange}
    allowClear
    format="YYYY-MM-DD"
  />

<div>
  {isDownloadingExcel ? (
    <Spin size="small" />
  ) : (
    <FileExcelOutlined
      title="Download Excel"
      style={{
        fontSize: 24,
        color: "#52c41a",
        cursor: "pointer",
      }}
      onClick={handleDownloadExcel}
    />
  )}
</div>
</div>

      <Table<CallLog>
        columns={columns}
        dataSource={callLogs?.data ?? []}
        loading={isPending}
        rowKey={(record) => record.caseId}
        pagination={false}
        size="middle"
        style={{ cursor: "pointer" }}
          onRow={(record) => ({
    onClick: () => {
      navigate(`/portfolio/${record.caseId}`);
    },
  })}
      />
      <Modal
  title="Call Recording"
  open={isAudioModalOpen}
  footer={null}
  destroyOnClose
    styles={{
    header: {
  
     marginTop:"10px"
    },
  }}
  onCancel={() => {
    setIsAudioModalOpen(false);
    setAudioUrl("");
  }}
>
  <audio
    controls
    autoPlay
    style={{ width: "100%" }}
    src={audioUrl}
  >
    Your browser does not support the audio element.
  </audio>
</Modal>
    </AppLayout>
  );
};

export default CallLogs;